import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import {
  initGoogleCalendarApi,
  isSignedIn as checkGoogleSignedIn,
  createLeadCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  getCalendarEventFieldName,
  loadGoogleScripts,
  setSelectedCalendar
} from '../utils/googleCalendar';

const GoogleCalendarContext = createContext(null);

export function GoogleCalendarProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // NOTE: Google Calendar settings were previously loaded from Firestore.
  // TODO: Migrate to Express API endpoint (GET /api/settings/integrations) when needed.
  // For now, settings remain null which disables the Google Calendar integration.


  // Initialize Google Calendar API when settings are loaded
  useEffect(() => {
    const initCalendar = async () => {
      if (!settings?.googleCalendarEnabled || !settings?.googleCalendarClientId || !settings?.googleCalendarApiKey) {
        setIsInitialized(false);
        setIsConnected(false);
        return;
      }

      try {
        await loadGoogleScripts();
        await initGoogleCalendarApi(settings.googleCalendarClientId, settings.googleCalendarApiKey);
        setIsInitialized(true);
        setIsConnected(checkGoogleSignedIn());

        // Set the selected calendar from settings
        if (settings.googleCalendarSelectedId) {
          setSelectedCalendar(settings.googleCalendarSelectedId);
        }
      } catch (error) {
        console.error('Failed to initialize Google Calendar:', error);
        setIsInitialized(false);
        setIsConnected(false);
      }
    };

    initCalendar();
  }, [settings?.googleCalendarEnabled, settings?.googleCalendarClientId, settings?.googleCalendarApiKey]);

  // Check if auto-sync is enabled for a specific event type
  const shouldAutoSync = useCallback((eventType) => {
    if (!settings?.googleCalendarEnabled || !settings?.googleCalendarAutoSync) return false;
    if (!isConnected) return false;

    switch (eventType) {
      case 'siteVisit':
        return settings.googleCalendarSyncSiteVisits;
      case 'call':
        return settings.googleCalendarSyncCalls;
      case 'event':
        return settings.googleCalendarSyncEvents;
      case 'booking':
        return settings.googleCalendarSyncBookings;
      default:
        return false;
    }
  }, [settings, isConnected]);

  // Sync a prequalification event to Google Calendar
  const syncLeadEvent = useCallback(async (prequalification, eventType, onUpdateLead) => {
    if (!shouldAutoSync(eventType)) return null;

    const eventFieldName = getCalendarEventFieldName(eventType);
    const existingEventId = prequalification[eventFieldName];

    try {
      // Determine if we should create, update, or delete
      let hasDate = false;
      switch (eventType) {
        case 'siteVisit':
          hasDate = !!prequalification.siteVisitDate;
          break;
        case 'call':
          hasDate = !!prequalification.nextCallDate;
          break;
        case 'event':
        case 'booking':
          hasDate = !!prequalification.eventDate;
          break;
      }

      if (!hasDate) {
        // If date is removed, delete the calendar event
        if (existingEventId) {
          await deleteCalendarEvent(existingEventId);
          if (onUpdateLead) {
            await onUpdateLead(prequalification.id, { [eventFieldName]: null });
          }
        }
        return null;
      }

      if (existingEventId) {
        // Update existing event
        const eventData = getEventData(prequalification, eventType);
        await updateCalendarEvent(existingEventId, eventData);
        return existingEventId;
      } else {
        // Create new event
        const result = await createLeadCalendarEvent(prequalification, eventType);
        if (result?.id && onUpdateLead) {
          await onUpdateLead(prequalification.id, { [eventFieldName]: result.id });
        }
        return result?.id;
      }
    } catch (error) {
      console.error('Calendar sync error:', error);
      return null;
    }
  }, [shouldAutoSync]);

  // Remove a calendar event for a prequalification
  const removeLeadEvent = useCallback(async (prequalification, eventType, onUpdateLead) => {
    const eventFieldName = getCalendarEventFieldName(eventType);
    const existingEventId = prequalification[eventFieldName];

    if (!existingEventId) return;

    try {
      await deleteCalendarEvent(existingEventId);
      if (onUpdateLead) {
        await onUpdateLead(prequalification.id, { [eventFieldName]: null });
      }
    } catch (error) {
      console.error('Failed to remove calendar event:', error);
    }
  }, []);

  const value = {
    isEnabled: settings?.googleCalendarEnabled || false,
    isConnected,
    isInitialized,
    shouldAutoSync,
    syncLeadEvent,
    removeLeadEvent,
    settings
  };

  return (
    <GoogleCalendarContext.Provider value={value}>
      {children}
    </GoogleCalendarContext.Provider>
  );
}

export function useGoogleCalendar() {
  const context = useContext(GoogleCalendarContext);
  if (!context) {
    throw new Error('useGoogleCalendar must be used within a GoogleCalendarProvider');
  }
  return context;
}

// Helper to get event data based on type
function getEventData(prequalification, eventType) {
  switch (eventType) {
    case 'siteVisit':
      return {
        title: `🏠 Site Visit: ${prequalification.clientName}`,
        description: `Client: ${prequalification.clientName}\nPhone: ${prequalification.phone || 'N/A'}\nStage: ${prequalification.stage}\n\nNotes:\n${prequalification.notes || 'No notes'}`,
        location: 'AAA FM Banquet Hall',
        startDate: prequalification.siteVisitDate,
        startTime: prequalification.siteVisitTime || null,
        durationMinutes: 60
      };

    case 'call':
      return {
        title: `📞 Call: ${prequalification.clientName}`,
        description: `Client: ${prequalification.clientName}\nPhone: ${prequalification.phone || 'N/A'}\nStage: ${prequalification.stage}\n\nLast Call Notes:\n${prequalification.lastCallNotes || 'No previous notes'}`,
        startDate: prequalification.nextCallDate,
        startTime: prequalification.nextCallTime || '10:00',
        durationMinutes: 15
      };

    case 'event':
      return {
        title: `🎉 Event: ${prequalification.clientName} - ${prequalification.eventType || 'Booking'}`,
        description: `Client: ${prequalification.clientName}\nPhone: ${prequalification.phone || 'N/A'}\nEvent Type: ${prequalification.eventType || 'N/A'}\nGuests: ${prequalification.guests || 'N/A'}\nAmount: PKR ${prequalification.amount?.toLocaleString() || 'N/A'}\n\nNotes:\n${prequalification.notes || 'No notes'}`,
        location: 'AAA FM Banquet Hall',
        startDate: prequalification.eventDate,
        durationMinutes: 480
      };

    case 'booking':
      return {
        title: `✅ BOOKED: ${prequalification.clientName} - ${prequalification.eventType || 'Event'}`,
        description: `CONFIRMED BOOKING\n\nClient: ${prequalification.clientName}\nPhone: ${prequalification.phone || 'N/A'}\nEvent Type: ${prequalification.eventType || 'N/A'}\nGuests: ${prequalification.guests || 'N/A'}\nAmount: PKR ${prequalification.amount?.toLocaleString() || 'N/A'}\n\nNotes:\n${prequalification.notes || 'No notes'}`,
        location: 'AAA FM Banquet Hall',
        startDate: prequalification.eventDate,
        durationMinutes: 480
      };

    default:
      return null;
  }
}
